import { storeToRefs } from 'pinia';
import { ComputedRef, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouteRecordRaw, useRoute, useRouter } from 'vue-router';

import { NavigationEntry } from '@silae/components';
import { useLocalBoolean } from '@silae/composables';
import { getValue } from '@silae/helpers';
import { NavigationEntriesOptions } from '~/domain';
import { useOrganizationStore } from '~/stores';

export function useNavigation(options: ComputedRef<NavigationEntriesOptions>): {
	appNavTitle: ComputedRef<string>;
	entries: ComputedRef<Array<NavigationEntry>>;
	isCollapsed: ComputedRef<boolean>;
	rail: ComputedRef<boolean>;
	asNavigationEntry: (route: RouteRecordRaw) => NavigationEntry;
	toggleCollapse: () => boolean;
	toggleRail: () => boolean;
} {
	const { value: isCollapsed, toggle: toggleCollapse } = useLocalBoolean(`ce-is-collapsed`);
	const { value: rail, toggle: toggleRail } = useLocalBoolean(`ce-rail`);
	const { t } = useI18n();
	const route = useRoute();
	const router = useRouter();
	const currentRouteName = computed(() => route.name as string);

	const orgStore = useOrganizationStore();
	const { organization, orgId } = storeToRefs(orgStore);
	const appNavTitle = computed(() => organization.value?.name);

	const isActiveRoute = (candidate: RouteRecordRaw, activeRouteName: string): boolean =>
		activeRouteName === candidate.name || candidate.children?.some(childRoute => isActiveRoute(childRoute, activeRouteName));

	const asNavigationEntry = (route: RouteRecordRaw): NavigationEntry => ({
		name: route.name as string,
		label: () => {
			if (route.meta?.label != undefined) {
				const val = getValue(route.meta.label);
				return t(val) ?? '';
			}
			return '';
		},
		icon: route.meta?.icon,
		active: isActiveRoute(route, currentRouteName.value),
		to: route,
		children: route.meta.isNavGroup ? route.children?.map(asNavigationEntry) : undefined
	});

	/**
	 * This computed "authorizationNavRouteParamContext"
	 * is a necessary Route context to calculate user authorizations.
	 * (if user can access given route / see its related menu entry)
	 *
	 * INFO: You might want to add some more context for new routes.
	 * We want to use minimum external deps in order to refine "entries" re-computations
	 */
	const authorizationNavRouteParamContext = computed(() => {
		return {
			orgId: orgId.value
		};
	});

	const entries: ComputedRef<Array<NavigationEntry>> = computed(() => {
		return router
			.getRoutes()
			.filter(route => {
				const needAuthentication = !!route.meta?.auth;
				const displayedAsNavEntry = !!(options.value?.filterByNavEntry && route.meta?.isNavEntry);
				const displayedAsSettingsNavEntry = !!(options.value?.filterBySettingsNavEntry && route.meta?.isSettingsNavEntry);
				const displayedAsAdminNavEntry = !!(options.value?.filterByAdminNavEntry && route.meta?.isAdminNavEntry);
				const isAuthorized = !!(route.meta?.isAuthorized?.(authorizationNavRouteParamContext.value) ?? true);

				return (
					needAuthentication && (displayedAsNavEntry || displayedAsSettingsNavEntry || displayedAsAdminNavEntry) && isAuthorized
				);
			})
			.sort((route, other) => route.meta?.order - other.meta?.order)
			.map(asNavigationEntry);
	});

	return {
		/** Refs and computed */
		appNavTitle,
		entries,
		isCollapsed,
		rail,

		/** Functions */
		asNavigationEntry,
		toggleCollapse,
		toggleRail
	};
}
